import { useEffect, useRef, useState } from "react";
import PeakHalfOval from "../onboarding/PeakHalfOval";
import { DEMO_PEAK_SCORE, PEAK_SCORE_SCALE_MAX, peakScoreZone } from "../onboarding/onboardingCopy";

interface PeakCardProps {
  title: string;
  summary: string;
  detail: string;
  bgColor: string;
}

/** Same bowl, zone readout and slider as the onboarding readiness screen. */
const InteractivePeakScore = () => {
  const [score, setScore] = useState(DEMO_PEAK_SCORE);
  const [seen, setSeen] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const zone = peakScoreZone(Math.round(score));

  useEffect(() => {
    const node = componentRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={componentRef} className="flex w-full max-w-sm flex-col gap-3">
      {seen ? (
        <PeakHalfOval
          progress={Math.round(score) / PEAK_SCORE_SCALE_MAX}
          color={zone.color}
          animateIn
        >
          <span className="font-geist text-[56px] font-bold leading-none tabular-nums text-base-content">
            {Math.round(score)}
          </span>
          <span className="font-grotesk text-[13px] font-semibold text-base-content">Peak Readiness</span>
        </PeakHalfOval>
      ) : (
        <div style={{ aspectRatio: "340 / 200" }} />
      )}
      <div className="flex flex-col gap-1 text-center">
        <p className="font-grotesk text-xl font-semibold transition-colors duration-300" style={{ color: zone.color }}>
          {zone.name}&nbsp;&nbsp;·&nbsp;&nbsp;{zone.range}
        </p>
        <p className="min-h-[44px] font-grotesk text-sm text-base-content/70">{zone.detail}</p>
      </div>
      <div className="flex flex-col gap-1">
        <input
          type="range"
          min={0}
          max={PEAK_SCORE_SCALE_MAX}
          step={1}
          value={score}
          onChange={(e) => setScore(Number(e.target.value))}
          aria-label="Explore readiness zones"
          className="w-full"
          style={{ accentColor: zone.color }}
        />
        <div className="flex justify-between font-grotesk text-[11px] text-base-content/60">
          <span>0</span>
          <span>Drag to explore your zones</span>
          <span>{PEAK_SCORE_SCALE_MAX}</span>
        </div>
      </div>
    </div>
  );
};

const PeakCard = ({ title, summary, detail, bgColor }: PeakCardProps) => (
  <div
    className="flex flex-1 flex-col rounded-2xl p-8"
    style={{
      background: `linear-gradient(165deg, ${bgColor} 0%, color-mix(in srgb, ${bgColor} 62%, black) 100%)`,
    }}
  >
    <h3 className="text-white font-bold text-3xl mb-2 font-heading">{title}</h3>
    <p className="text-white/95 text-base leading-snug font-grotesk mb-2">{summary}</p>
    <p className="text-white/75 text-sm leading-relaxed font-grotesk">{detail}</p>
  </div>
);

const peakFactors = [
  {
    title: "Load",
    summary: "What training you've been exposed to vs. what you're used to.",
    detail:
      "Compares your recent acute training load against your chronic baseline to detect spikes or drops.",
    bgColor: "#374151",
  },
  {
    title: "Wellness",
    summary: "How you feel and respond to training daily.",
    detail:
      "Subjective wellness check-ins capture how your body is experiencing training stress.",
    bgColor: "#15803d",
  },
  {
    title: "Physiology",
    summary: "How your physiology is responding over time.",
    detail:
      "Pulled from wearable data (HRV, RHR, respiratory rate) to measure cardiorespiratory adaptation.",
    bgColor: "#6b21a8",
  },
];

export default function PeakScoreSection() {
  return (
    <div className="bg-base-200 py-24 flex flex-col items-center px-6">
      <div className="flex flex-col max-w-5xl mx-auto gap-16 md:gap-24 w-full">
        {/* Hero: Score + Intro */}
        <div className="flex flex-col md:flex-row gap-10 md:gap-12 items-center w-full">
          <div className="flex justify-center md:flex-1 order-1 md:order-2">
            <InteractivePeakScore />
          </div>

          <div className="flex flex-col gap-5 justify-center md:flex-[2] order-2 md:order-1">
            <div>
              <p className="text-base text-left font-grotesk text-base-content/60 font-medium mb-1">
                The science behind the
              </p>
              <h2 className="text-4xl md:text-5xl font-bold font-grotesk text-left leading-tight">
                Peak <span className="font-light">Readiness Score</span>
              </h2>
            </div>

            <p className="text-lg text-left text-base-content/80 font-grotesk leading-relaxed">
              A daily readiness signal designed for athletes to make better
              training decisions. It reflects how prepared you are to tolerate
              training stress and express performance on a given day.
            </p>

            <p className="text-base text-left font-grotesk leading-relaxed font-medium text-base-content">
              The Peak Score captures what wearables cannot alone, the context.
              What you are actually doing for training and how you feel.
            </p>

            <p className="text-sm text-left text-base-content/60 font-grotesk leading-relaxed">
              The score is relative to you, not a prediction of performance or
              a comparison to other athletes. It tells you if today's training
              demands are aligned with your current state.
            </p>

            <div>
              <a
                href="https://learn.peak.app/what-is-the-peak-score/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-base-300 text-base-content/70 py-2.5 px-6 rounded-full font-grotesk text-sm hover:bg-base-300 transition-colors font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                Learn more
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="w-4 h-4"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.22 14.78a.75.75 0 0 0 1.06 0l7.22-7.22v5.69a.75.75 0 0 0 1.5 0v-7.5a.75.75 0 0 0-.75-.75h-7.5a.75.75 0 0 0 0 1.5h5.69l-7.22 7.22a.75.75 0 0 0 0 1.06Z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* How it's calculated — Combined Factor Cards */}
        <div>
          <h3 className="text-4xl md:text-5xl font-bold font-grotesk text-left mb-3">
            How it's calculated
          </h3>
          <p className="text-lg md:text-xl text-left text-base-content/70 font-grotesk leading-relaxed mb-8 max-w-2xl">
            The Peak Score is based on three realities that matter most for
            readiness.
          </p>

          <div className="flex flex-col md:flex-row gap-4 w-full">
            {peakFactors.map((factor) => (
              <PeakCard
                key={factor.title}
                title={factor.title}
                summary={factor.summary}
                detail={factor.detail}
                bgColor={factor.bgColor}
              />
            ))}
          </div>

          <p className="text-xs text-base-content/50 font-grotesk mt-4">
            * The Physiology factor uses wearable data when available but is not
            required. It enhances the depth of insights provided.
          </p>
        </div>

        {/* Expert Quote */}
        <figure className="pb-16 mt-4 md:mt-8">
          <blockquote className="flex flex-col gap-6 relative pl-6 border-l-4 border-base-300">
            <p className="text-xl sm:text-2xl md:text-3xl font-medium font-grotesk text-base-content/80 leading-snug">
              The Peak score gives athletes a more holistic understanding of
              readiness that wearables alone miss. Combining{" "}
              <strong className="text-base-content">
                training load, subjective wellness, and physiology from wearable
                data
              </strong>
              , increases an athlete's confidence in daily readiness indicators.
            </p>
          </blockquote>
          <figcaption className="mt-6 pl-6">
            <p className="text-lg font-semibold font-grotesk">Sam Leslie</p>
            <p className="text-sm text-base-content/60 font-grotesk">
              Head of Sports Science, Sports Physiotherapist & Olympic Coach
            </p>
            <p className="text-xs text-base-content/40 font-grotesk mt-1">
              30 years experience as physiotherapist & researcher
            </p>
          </figcaption>
        </figure>
      </div>
    </div>
  );
}
